/**
 * Pure pieces of the listing engagement signals (recommender plan,
 * section 2.2), kept free of React and the DOM so the unit tests can
 * drive them with a fake clock:
 *
 *  - createDwell: milliseconds a listing was on screen while the tab was
 *    visible (`listing.close` `ms`).
 *  - scrollMarks: the 25/50/75/100% marks a listing page's scroll passed
 *    (`listing.scroll` `pct`).
 *  - filterDiff: what one filter change did (`listings.filters` `diff`).
 */

/** One hour: a drawer left open overnight on a visible tab says nothing
 *  more than an hour does (the backend caps at the same value). */
export const MAX_DWELL_MS = 60 * 60 * 1000;

export interface Dwell {
  /** The tab became visible (true) or hidden (false). */
  setVisible(visible: boolean): void;
  /** Visible milliseconds since the start or the last take(), then
   *  starts counting again from zero. */
  take(): number;
  /** Visible milliseconds so far, without resetting. */
  peek(): number;
}

export function createDwell(now: () => number, visible: boolean): Dwell {
  let banked = 0;
  let since: number | null = visible ? now() : null;
  const running = () => (since == null ? 0 : Math.max(0, now() - since));
  return {
    setVisible(v) {
      if (v && since == null) since = now();
      else if (!v && since != null) {
        banked += running();
        since = null;
      }
    },
    take() {
      const ms = Math.min(MAX_DWELL_MS, banked + running());
      banked = 0;
      if (since != null) since = now();
      return Math.round(ms);
    },
    peek() {
      return Math.min(MAX_DWELL_MS, banked + running());
    },
  };
}

export const SCROLL_MARKS = [25, 50, 75, 100] as const;

/** The marks newly passed at this scroll position, in order. The bottom
 *  of the viewport counts (the first screen is read too); a page shorter
 *  than its viewport reaches 100 at once. `done` is what was sent. */
export function scrollMarks(scrollY: number, viewport: number, docHeight: number, done: ReadonlySet<number>): number[] {
  if (docHeight <= 0) return [];
  const pct = Math.min(100, ((scrollY + viewport) / docHeight) * 100);
  // A pixel short of the bottom is the bottom (rounding, mobile bars).
  const reached = docHeight - (scrollY + viewport) <= 2 ? 100 : pct;
  return SCROLL_MARKS.filter((m) => reached >= m && !done.has(m));
}

type Params = URLSearchParams | Record<string, string | string[] | null | undefined>;

function valuesOf(p: Params, key: string): string[] {
  if (p instanceof URLSearchParams) return p.getAll(key).filter(Boolean);
  const v = p[key];
  if (v == null) return [];
  return (Array.isArray(v) ? v : [v]).filter(Boolean);
}

function keysOf(p: Params): string[] {
  return p instanceof URLSearchParams ? [...new Set(p.keys())] : Object.keys(p);
}

/** Navigation, not intent. */
const IGNORED = new Set(["page", "sort", "layer"]);

/**
 * The changed filters, compact: `rooms=2|3;price_max=150000;-floor_min`
 * (a set key with its values joined by "|", a cleared key with a minus),
 * keys in alphabetical order, at most `max` characters (whole entries
 * only). Empty when nothing a filter says changed.
 */
export function filterDiff(prev: Params, next: Params, max = 200): string {
  const keys = [...new Set([...keysOf(prev), ...keysOf(next)])].filter((k) => !IGNORED.has(k)).sort();
  const parts: string[] = [];
  for (const k of keys) {
    const a = valuesOf(prev, k);
    const b = valuesOf(next, k);
    if (a.length === b.length && a.every((v, i) => v === b[i])) continue;
    parts.push(b.length ? `${k}=${b.map((v) => v.slice(0, 40)).join("|")}` : `-${k}`);
  }
  let out = "";
  for (const p of parts) {
    const add = out ? `;${p}` : p;
    if (out.length + add.length > max) break;
    out += add;
  }
  return out;
}
